// src/components/brandCustomization/EmailCustomization/EmailTemplateSelector.jsx
import React, { useState, useMemo } from 'react';
import { FaEnvelope, FaUserTie, FaUsers, FaUserClock, FaSearch, FaCheckCircle } from 'react-icons/fa';
import { emailTemplateInfo } from '../../../utils/brandCustomization';

/**
 * EmailTemplateSelector - Selector visual de templates de email
 * Muestra un grid de cards pequeñas para seleccionar qué email personalizar
 */
const EmailTemplateSelector = ({
    templates,
    selectedTemplate,
    onSelect,
    formData = {}
}) => {
    const [searchQuery, setSearchQuery] = useState('');

    // Iconos por tipo de template
    const icons = {
        ticketCreation: FaEnvelope,
        ticketResend: FaEnvelope,
        addFranchise: FaUserTie,
        addWorker: FaUsers,
        winback: FaUserClock
    };

    // Filtrar templates según búsqueda
    const filteredTemplates = useMemo(() => {
        if (!searchQuery.trim()) return templates;

        const query = searchQuery.toLowerCase();
        return templates.filter(templateName => {
            const info = emailTemplateInfo[templateName];
            if (!info) return false;

            return (
                info.name.toLowerCase().includes(query) ||
                info.description.toLowerCase().includes(query) ||
                templateName.toLowerCase().includes(query)
            );
        });
    }, [templates, searchQuery]);

    return (
        <div className="mb-8">
            {/* Header con contador */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                        Selecciona un Email para Personalizar
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                        {templates.length} {templates.length === 1 ? 'template disponible' : 'templates disponibles'}
                    </p>
                </div>
            </div>

            {/* Buscador (solo si hay más de 5 templates) */}
            {templates.length > 5 && (
                <div className="mb-4">
                    <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Buscar email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>
            )}

            {/* Grid de templates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredTemplates.map(templateName => {
                    const info = emailTemplateInfo[templateName];
                    if (!info) return null;

                    const Icon = icons[templateName] || FaEnvelope;
                    const isSelected = selectedTemplate === templateName;
                    const templateData = formData[templateName] || {};
                    const isActive = templateData.isActive !== false;

                    return (
                        <button
                            key={templateName}
                            type="button"
                            onClick={() => onSelect(templateName)}
                            className={`
                relative p-4 rounded-lg border-2 transition-all text-left
                ${isSelected
                                    ? 'border-blue-500 bg-blue-50 shadow-md'
                                    : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                                }
              `}
                        >
                            {/* Indicador de selección */}
                            {isSelected && (
                                <div className="absolute top-2 right-2">
                                    <FaCheckCircle className="text-blue-500" />
                                </div>
                            )}

                            {/* Icono */}
                            <div className={`
                w-12 h-12 rounded-lg flex items-center justify-center mb-3
                ${isSelected ? 'bg-blue-100' : 'bg-gray-100'}
              `}>
                                <Icon className={`
                  text-xl
                  ${isSelected ? 'text-blue-600' : 'text-gray-600'}
                `} />
                            </div>

                            {/* Nombre */}
                            <h4 className={`
                font-semibold text-sm mb-1
                ${isSelected ? 'text-blue-900' : 'text-gray-900'}
              `}>
                                {info.name}
                            </h4>

                            {/* Descripción */}
                            <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                                {info.description}
                            </p>

                            {/* Estado activo/inactivo y personalización */}
                            <div className="flex items-center justify-between mt-2">
                                <div className="flex items-center gap-2">
                                    <span className={`
                    w-2 h-2 rounded-full
                    ${isActive ? 'bg-green-500' : 'bg-gray-300'}
                  `} />
                                    <span className="text-xs text-gray-500">
                                        {isActive ? 'Activo' : 'Inactivo'}
                                    </span>
                                </div>
                                {/* Indicador si tiene personalización */}
                                {templateData.texts || templateData.styles ? (
                                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                        Personalizado
                                    </span>
                                ) : (
                                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                        Por defecto
                                    </span>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Mensaje si no hay resultados */}
            {filteredTemplates.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                    <FaSearch className="mx-auto text-3xl mb-2 opacity-50" />
                    <p>No se encontraron emails que coincidan con "{searchQuery}"</p>
                </div>
            )}
        </div>
    );
};

export default EmailTemplateSelector;

